import React, { Component } from "react";
import SEO from "react-seo-component";
import { Badge, Button, Col, Container, Row } from "reactstrap";
import AppDownloadLinks from "../../components/common/AppDownloadLinks";

class Section extends Component {
  scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  render() {
    return (
      <React.Fragment>
        <SEO
          title={"Get the QikTruck App — Free promo on your first trip"}
          titleTemplate={"QikTruck"}
          titleSeparator={`-`}
          description={
            "Download the QikTruck customer app and enjoy a free promo on your first booking. Book, track and manage deliveries with Africa's leading logistics platform."
          }
          image={"https://media.qiktruck.co.za/customer-app.png"}
          pathname={"https://qiktruck.co.za/promo"}
          twitterUsername={"qiktrucksa"}
        />

        {/* Hero: App Download + First Trip Promo */}
        <section id="home" className="app-hero">
          <div className="hero-gradient-overlay">
            <Container>
              <Row className="align-items-center min-vh-100">
                <Col lg={6} md={12}>
                  <div className="hero-content text-white">
                    <Badge color="warning" pill className="mb-3 px-3 py-2">
                      🎉 New to QikTruck
                    </Badge>
                    <h1 className="hero-title mb-3 text-white">
                      Download the QikTruck App
                    </h1>
                    <p className="hero-subtitle mb-2">Get a free promo on your first trip</p>
                    <p className="hero-description mb-4">
                      Book a bakkie or truck in minutes. Transparent pricing, live tracking, and
                      24/7 support — all in one powerful app.
                    </p>

                    <div className="hero-actions mb-4">
                      <Button
                        color="light"
                        size="lg"
                        className="me-3 mb-3 text-dark"
                        onClick={() => this.scrollToSection("download")}
                      >
                        Download Free
                      </Button>
                      <Button
                        color="outline-light"
                        size="lg"
                        className="mb-3"
                        onClick={() => this.scrollToSection("how-it-works")}
                      >
                        How It Works
                      </Button>
                    </div>

                    <div className="store-badges">
                      <AppDownloadLinks />
                    </div>

                    <p className="terms text-white-50 mt-3 mb-0">
                      T&Cs apply. Limited-time welcome offer for new customers only.
                    </p>
                  </div>
                </Col>
                <Col lg={6} md={12}>
                  <div className="hero-image-container">
                    <div className="phone-mockup">
                      <img
                        src="https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80"
                        alt="QikTruck Customer App Interface"
                        className="app-screenshot"
                      />
                    </div>
                    <div className="floating-card">
                      <div className="card-content">
                        <h5>First Trip Promo</h5>
                        <p>Automatic at checkout for eligible users</p>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            </Container>
          </div>
        </section>

        {/* How It Works */}
        <section id="how-it-works" className="py-5 bg-light">
          <Container>
            <Row className="justify-content-center text-center mb-4">
              <Col lg={8}>
                <h2 className="section-title">How It Works</h2>
                <p className="section-subtitle">
                  Getting started takes just a few minutes
                </p>
              </Col>
            </Row>
            <Row>
              <Col lg={4} md={6} className="mb-4">
                <div className="step-card h-100">
                  <div className="icon-wrapper mb-3">
                    <i className="fas fa-download text-warning" style={{ fontSize: "2.2rem" }}></i>
                  </div>
                  <h4>Download the App</h4>
                  <p>Install QikTruck from the App Store or Google Play.</p>
                </div>
              </Col>
              <Col lg={4} md={6} className="mb-4">
                <div className="step-card h-100">
                  <div className="icon-wrapper mb-3">
                    <i className="fas fa-user-check text-warning" style={{ fontSize: "2.2rem" }}></i>
                  </div>
                  <h4>Create Your Account</h4>
                  <p>Sign up in seconds and set up your pickup and drop-off.</p>
                </div>
              </Col>
              <Col lg={4} md={6} className="mb-4">
                <div className="step-card h-100">
                  <div className="icon-wrapper mb-3">
                    <i className="fas fa-truck-moving text-warning" style={{ fontSize: "2.2rem" }}></i>
                  </div>
                  <h4>Book Your First Trip</h4>
                  <p>Promo applies on first booking for eligible users. Easy.</p>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Benefits */}
        <section id="benefits" className="py-5">
          <Container>
            <Row className="justify-content-center text-center mb-4">
              <Col lg={8}>
                <h2 className="section-title">Why Choose QikTruck?</h2>
                <p className="section-subtitle">
                  Powerful logistics in your pocket
                </p>
              </Col>
            </Row>
            <Row>
              <Col lg={3} md={6} className="mb-4">
                <div className="benefit-card h-100">
                  <i className="fas fa-bolt text-warning mb-3" style={{ fontSize: "2rem" }}></i>
                  <h5>Instant Quotes</h5>
                  <p>Transparent pricing with no hidden fees.</p>
                </div>
              </Col>
              <Col lg={3} md={6} className="mb-4">
                <div className="benefit-card h-100">
                  <i className="fas fa-map-marked-alt text-warning mb-3" style={{ fontSize: "2rem" }}></i>
                  <h5>Live Tracking</h5>
                  <p>Track your delivery in real-time.</p>
                </div>
              </Col>
              <Col lg={3} md={6} className="mb-4">
                <div className="benefit-card h-100">
                  <i className="fas fa-shield-alt text-warning mb-3" style={{ fontSize: "2rem" }}></i>
                  <h5>Secure Payments</h5>
                  <p>Multiple payment options with bank-grade security.</p>
                </div>
              </Col>
              <Col lg={3} md={6} className="mb-4">
                <div className="benefit-card h-100">
                  <i className="fas fa-headset text-warning mb-3" style={{ fontSize: "2rem" }}></i>
                  <h5>24/7 Support</h5>
                  <p>We are here anytime you need help.</p>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Download CTA */}
        <section id="download" className="py-5 bg-light">
          <Container>
            <Row className="justify-content-center text-center">
              <Col lg={8}>
                <h2 className="section-title mb-3">Get the App Now</h2>
                <p className="section-subtitle mb-4">
                  Join thousands of customers moving with QikTruck today
                </p>
                <div className="download-buttons mb-4">
                  <AppDownloadLinks />
                </div>
                <div className="qr-section">
                  <p className="mb-2">Prefer to scan?</p>
                  <div className="qr-code-placeholder">
                    <i className="fas fa-qrcode" style={{ fontSize: "3rem", color: "#6b7280" }}></i>
                    <p className="mt-2 text-muted">QR Code</p>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Support */}
        <section id="support" className="py-5">
          <Container>
            <Row className="justify-content-center text-center">
              <Col lg={8}>
                <h3>Need help getting started?</h3>
                <p className="mb-3">Our customer success team is ready to help.</p>
                <div className="contact-links">
                  <a className="me-3" href="mailto:support@qiktruck.co.za" target="_blank" rel="noopener noreferrer">
                    <i className="mdi mdi-email" style={{ marginRight: 6 }}></i>
                    support@qiktruck.co.za
                  </a>
                  <a href="https://wa.me/+27663784460" target="_blank" rel="noopener noreferrer">
                    <i className="mdi mdi-whatsapp" style={{ marginRight: 6 }}></i>
                    WhatsApp Support
                  </a>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        <style jsx>{`
          .app-hero {
            background: linear-gradient(135deg, #0ea5e9 0%, #1e293b 100%);
            position: relative;
            overflow: hidden;
          }
          .hero-content { padding-top: 12px; padding-bottom: 12px; }
          .hero-actions {
            display: flex;
            flex-wrap: wrap;
            gap: 12px;
          }
          .hero-actions .btn {
            padding-left: 20px;
            padding-right: 20px;
          }
          .hero-gradient-overlay {
            background: rgba(0,0,0,0.15);
            min-height: 100vh;
            display: flex;
            align-items: center;
          }
          .hero-title {
            font-size: 3rem;
            font-weight: 800;
            line-height: 1.1;
          }
          .hero-subtitle {
            font-size: 1.25rem;
            font-weight: 700;
            opacity: 0.95;
          }
          .hero-description {
            font-size: 1.05rem;
            opacity: 0.9;
            line-height: 1.6;
          }
          .phone-mockup {
            position: relative;
            max-width: 320px;
            margin: 0 auto;
          }
          .app-screenshot {
            width: 100%;
            height: 520px;
            object-fit: cover;
            border-radius: 30px;
            box-shadow: 0 25px 50px rgba(0,0,0,0.3);
            border: 8px solid #111827;
          }
          .floating-card {
            position: absolute;
            bottom: 20px;
            right: -20px;
            background: white;
            padding: 14px 18px;
            border-radius: 14px;
            box-shadow: 0 10px 30px rgba(0,0,0,0.2);
            min-width: 160px;
          }
          .section-title {
            font-size: 2.2rem;
            font-weight: 700;
            color: #1f2937;
            margin-bottom: 0.5rem;
          }
          .section-subtitle {
            font-size: 1.1rem;
            color: #6b7280;
            margin-bottom: 1.5rem;
          }
          .step-card, .benefit-card {
            background: #ffffff;
            border-radius: 14px;
            padding: 24px;
            box-shadow: 0 12px 24px rgba(0,0,0,0.06);
            text-align: center;
          }
          .step-card h4, .benefit-card h5 { margin-bottom: 8px; }
          .step-card p, .benefit-card p { margin-bottom: 0; }
         
          .download-buttons { display: flex; flex-wrap: wrap; justify-content: center; gap: 12px; }
          .store-badges .app-download-links { justify-content: start; }
          .store-badges { margin-top: 8px; }
          .qr-code-placeholder {
            display: inline-block;
            padding: 16px 22px;
            border: 2px dashed #d1d5db;
            border-radius: 10px;
            background: #f9fafb;
          }
          .qr-section { margin-top: 16px; }
          .contact-links a {
            color: #111827;
            text-decoration: none;
          }
          .contact-links a:hover {
            text-decoration: underline;
          }
          .contact-links { display: inline-flex; gap: 16px; flex-wrap: wrap; align-items: center; justify-content: center; }
          @media (max-width: 992px) {
            .hero-title { font-size: 2.75rem; }
          }
          @media (max-width: 768px) {
            .hero-title { font-size: 2.25rem; }
            .app-screenshot { height: 420px; }
            .floating-card { position: static; margin-top: 16px; text-align: center; }
            .app-download-links { grid-template-columns: 1fr 1fr; }
          }
          @media (max-width: 576px) {
            .hero-gradient-overlay { min-height: auto; padding: 48px 0; }
            .hero-content { text-align: center; }
            .store-badges .app-download-links { justify-content: center; grid-template-columns: 1fr; }
            .hero-actions { flex-direction: column; align-items: stretch; }
            .hero-actions .btn { width: 100%; }
            .app-screenshot { height: 360px; }
            .section-title { font-size: 2rem; }
            .download-buttons { justify-content: center; }
            .contact-links { gap: 12px; }
          }
          @media (max-width: 420px) {
            .app-screenshot { height: 320px; }
            .section-title { font-size: 1.8rem; }
          }
        `}</style>
      </React.Fragment>
    );
  }
}

export default Section;
